import "../JSONTools/JSONformatter.css";
import "./TextUtilities.css";
import ToolHeader from "../../Components/ToolHeader";
import { Row, Col } from "react-bootstrap";
import { useState, useEffect, useMemo, useRef } from "react";
import { RandomStringAPI } from "../../Services/APIService";

import FormatterPanel from "../../HomePage/FormatterPanel";
import { OutputHeaderButtons } from "../JSONTools/JSONToolsHeaderButtons";
import { ReactComponent as RefreshIcon } from "../../Icons/Regenerate.svg";

const typeOptions = [
    {
        key: "LOWERCASE",
        title: "Lowercase Letters",
        value: "a-z"
    },
    {
        key: "UPPERCASE",
        title: "Uppercase Letters",
        value: "A-Z"
    },
    {
        key: "ALPHA",
        title: "Letters",
        value: "A-Z a-z"
    },
    {
        key: "NUMERIC",
        title: "Numbers",
        value: "0-9"
    },
    {
        key: "ALPHANUMERIC",
        title: "Alphanumeric",
        value: "A-Z a-z 0-9"
    },
    {
        key: "SPECIAL_CHARACTERS",
        title: "Special Characters",
        value: "!@#$"
    },
    {
        key: "MIXED",
        title: "Mixed",
        value: "A-z 0-9 !@#"
    },
    {
        key: "HEX",
        title: "Hex",
        value: "0-9 A-F"
    },
    {
        key: "CUSTOM",
        title: "Custom",
        value: "Custom set"
    }
];

function clampNumber(value, min, max) {
    return Math.min(Math.max(Number(value) || min, min), max);
}

function RandomStringGenerator() {
    const [values, setValues] = useState([]);
    const [processingTimeMs, setProcessingTimeMs] = useState(0);
    const [apiError, setApiError] = useState("");
    const [length, setLength] = useState(8);
    const [type, setType] = useState("MIXED");
    const [count, setCount] = useState(3);
    const [refreshKey, setRefreshKey] = useState(0);
    const randomRequestId = useRef(0);
    const toolFav = { toolName: "Random String Generator", Iconpath: "/FileCodeRaw.svg" };
    const quickLengths = [8, 16, 24, 32, 64];

    const payload = useMemo(() => ({
        length: clampNumber(length, 1, 100),
        type,
        count: clampNumber(count, 1, 1000)
    }), [length, type, count]);

    const outputText = values.join("\n");
    const lengthProgress = `${((payload.length - 1) / 99) * 100}%`;

    

    useEffect(() => {
        const requestId = randomRequestId.current + 1;
        randomRequestId.current = requestId;

        const timeoutId = setTimeout(async () => {
            try {
                const response = await RandomStringAPI(payload);

                if (requestId === randomRequestId.current) {
                    setValues(response.data.values || []);
                    setProcessingTimeMs(response.data.processingTimeMs);
                    setApiError("");
                }
            } catch (error) {
                if (requestId === randomRequestId.current) {
                    setApiError(error.response?.data?.message || error.message);
                }
            }
        }, 250);

        return () => clearTimeout(timeoutId);
    }, [payload, refreshKey]);

    

    const getComplexity = () => {
        const charsetSizes = {
            LOWERCASE: 26,
            UPPERCASE: 26,
            ALPHA: 52,
            NUMERIC: 10,
            ALPHANUMERIC: 62,
            SPECIAL_CHARACTERS: 32,
            MIXED: 90,
            HEX: 16,
            CUSTOM: 10
        };

        const charsetSize = charsetSizes[type] || 10;

        // Estimated entropy in bits: length * log2(charsetSize)
        const bits = Math.log2(Math.max(1, charsetSize)) * payload.length;

        // Thresholds (bits): <40 Weak, 40-59 Medium, 60-127 Strong, >=128 Very Strong
        let label = "Weak";
        let color = "#ff4d4d";

        if (bits >= 128) {
            label = "Very Strong";
            color = "#31d158";
        } else if (bits >= 60) {
            label = "Strong";
            color = "#31d158";
        } else if (bits >= 40) {
            label = "Medium";
            color = "#f5b942";
        } else {
            label = "Weak";
            color = "#ff4d4d";
        }

        const width = `${Math.min(100, Math.round((bits / 128) * 100))}%`;

        return { label: `${label}`, width, color, bits, charsetSize };
    };

    const complexity = getComplexity();

    return (
        <>
            <Col>
                <ToolHeader title="Random String Generator" description="Cryptographically secure random strings for keys, tokens & IDs" toolFav={toolFav} />

                <Row className='Formatter random-string-row'>
                    <Col xs={12} md ={4} lg={5} className="random-options-column">
                        <div className="password-card">
                            <div className="password-header">
                                Options
                            </div>

                            <div className="password-body">
                                <div className="section-label">
                                    STRING TYPE
                                </div>

                                <div className="character-grid">
                                    {typeOptions.map((item) => (
                                        <div
                                            key={item.key}
                                            className={`char-card ${type === item.key ? "active" : ""}`}
                                            onClick={() => setType(item.key)}
                                        >
                                            <div className="char-title">
                                                {item.title}
                                            </div>
                                            <div className="char-sub">
                                                {item.value}
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="length-header">
                                    <span className="section-label">
                                        LENGTH
                                    </span>
                                </div>

                                <div className="slider-wrapper">
                                    <input
                                        type="range"
                                        min="1"
                                        max="100"
                                        value={payload.length}
                                        onChange={(e) =>
                                            setLength(Number(e.target.value))
                                        }
                                        className="length-slider"
                                        style={{ "--slider-progress": lengthProgress }}
                                    />

                                    <div className="slider-badge">
                                        {payload.length}
                                    </div>
                                </div>

                                <div className="quick-lengths">
                                    {quickLengths.map((num) => (
                                        <button
                                            key={num}
                                            type="button"
                                            className={`quick-btn ${payload.length === num ? "active" : ""}`}
                                            onClick={() => setLength(num)}
                                        >
                                            {num}
                                        </button>
                                    ))}
                                </div>

                                <div className="length-header">
                                    <span className="section-label">
                                        COUNT
                                    </span>
                                    <input
                                        type="number"
                                        min="1"
                                        max="1000"
                                        value={count}
                                        onChange={(e) => setCount(e.target.value)}
                                        className="count-input"
                                    />
                                </div>

                                <div className="complexity-header">
                                    <span className="section-label">
                                        COMPLEXITY
                                    </span>
                                    <span
                                        className="complexity-label"
                                        style={{ color: complexity.color }}
                                    >
                                        {complexity.label}
                                    </span>
                                </div>

                                <div className="complexity-bar">
                                    <div
                                        className="complexity-fill"
                                        style={{
                                            width: complexity.width,
                                            background: complexity.color
                                        }}
                                    />
                                </div>

                                <div className="complexity-info">
                                    <span>Type: {payload.type}</span>
                                    <span>API time: {processingTimeMs} ms</span>
                                </div>

                                <button
                                    type="button"
                                    className="generate-btn"
                                    onClick={() => setRefreshKey((current) => current + 1)}
                                >
                                    <RefreshIcon className="refresh-icon" />
                                    Generate
                                </button>
                            </div>
                        </div>
                    </Col>
                    <Col xs={12} md = {8} lg={7} className="random-output-column">
                        <FormatterPanel
                            title="Generated strings will appear here"
                            value={outputText}
                            placeholder="output will appear here..."
                            readOnly
                            headerButtons={
                                <OutputHeaderButtons
                                    outputText={outputText}
                                    fileName="random-strings.txt"
                                    mimeType="text/plain;charset=utf-8"
                                />
                            }
                            footerLeft={`Strings : ${values.length}`}
                            footerRight={apiError ? `Error : ${apiError}` : `Size : ${new Blob([outputText]).size} B`}
                        />
                    </Col>
                </Row>
            </Col >
        </>
    );
}

export default RandomStringGenerator;
